import * as THREE from 'three';
window.THREE = THREE;

import {
    Mesh,
    DoubleSide,
    MeshBasicMaterial,
    Vector3,
    SphereGeometry,
    Sprite,
    SpriteMaterial,
} from 'three';

import { VRObject3D } from './VRObject3D';
import { cornerHandleLocalFromVertex, vertexLocalFromCornerHandle } from './helpers/worldHelpers';
import { textureLoader, linkTexture, myLocationTexture, dragTexture, meshTextures, emptyTexture, INDICATOR_HIT_SIZE } from './helpers/texturesAndGeometries';
import { ComponentRegistry } from '../../constants/componentRegistry.js';

/**
 * Max longer side for link-image file upload (matches VRVapiChairWithCSS3D /
 * FileDragDropUploadController pre-upload cap for agent images).
 */
const MAX_LINK_UPLOAD_IMAGE_LONG_SIDE_PX = 1024;

/**
 * Downscale an image File/Blob so its longer side is at most maxSidePx, then pass result to done().
 * Same algorithm as FileDragDropUploadController agent-image transformFile.
 * @param {File|Blob} file
 * @param {(out: File|Blob) => void} done
 * @param {number} [maxSidePx]
 */
function transformImageFileToMaxLongSide(file, done, maxSidePx = MAX_LINK_UPLOAD_IMAGE_LONG_SIDE_PX) {
    const isImageFile = file && typeof file.type === 'string' && file.type.indexOf('image/') === 0;
    if (!isImageFile) {
        done(file);
        return;
    }

    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        if (!width || !height) {
            done(file);
            return;
        }

        const largestSide = Math.max(width, height);
        if (largestSide <= maxSidePx) {
            done(file);
            return;
        }

        const ratio = maxSidePx / largestSide;
        const targetWidth = Math.max(1, Math.round(width * ratio));
        const targetHeight = Math.max(1, Math.round(height * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            done(file);
            return;
        }

        ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

        const outType = (file.type === 'image/png' || file.type === 'image/webp')
            ? file.type
            : 'image/jpeg';

        canvas.toBlob((blob) => {
            if (!blob) {
                done(file);
                return;
            }
            done(blob);
        }, outType, 0.9);
    };
    image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        done(file);
    };
    image.src = objectUrl;
}


class VRLinkObject extends VRObject3D {

    /** Same cap as VRVapiChairWithCSS3D.MAX_AGENT_IMAGE_DIMENSION_PX / dropzone agent uploads. */
    static MAX_UPLOAD_IMAGE_LONG_SIDE_PX = MAX_LINK_UPLOAD_IMAGE_LONG_SIDE_PX;

    /**
     * Dropzone transformFile hook: replace oversized images with a downscaled blob before upload.
     * @param {File|Blob} file
     * @param {(out: File|Blob) => void} done
     */
    static transformImageFileForUpload = (file, done) => {
        transformImageFileToMaxLongSide(file, done, VRLinkObject.MAX_UPLOAD_IMAGE_LONG_SIDE_PX);
    };

    constructor(containerName, camera, texture, x, y, z, model) {

        super(containerName);

        this.camera = camera;
        this.link = '';
        this.material = new MeshBasicMaterial({
            map: texture,
            side: DoubleSide,
            transparent: true,
            depthWrite: false,
            alphaTest: 0.02,
            premultipliedAlpha: true,
        });
        this.geometry = new THREE.PlaneGeometry(64, 64);
        this.mesh = new Mesh(this.geometry, this.material);
        this.mesh.renderOrder = 10;
        this.mesh.type = 'webLink';
        this.mesh.name = 'webLink';
        this.name = 'webLink';
        this.type = 'webLink';

        // Drag configuration (match gallery / video-player useIcon behavior)
        this.objConfig.dragOnlyWhenSelected = false;
        this.objConfig.isResizable = true;
        this.objConfig.isRotatable = false;
        this.objConfig.cubeSize = 20;
        this.objConfig.offset = {
            topLeft: { x: 2, y: -3.5 },
            topRight: { x: -2, y: -3.5 },
            bottomRight: { x: -2, y: 3.5 },
            bottomLeft: { x: 2, y: 3.5 },
        };

        this.perspectivePosition = {
            topLeft: new Vector3(-24, 24, 0),
            topRight: new Vector3(24, 24, 0),
            bottomRight: new Vector3(24, -24, 0),
            bottomLeft: new Vector3(-24, -24, 0),
        };
        this.originalPerspectivePosition = {
            topLeft: this.perspectivePosition.topLeft.clone(),
            topRight: this.perspectivePosition.topRight.clone(),
            bottomRight: this.perspectivePosition.bottomRight.clone(),
            bottomLeft: this.perspectivePosition.bottomLeft.clone(),
        };

        this.dragCubeCorners = {};
        this.dragSprites = {};
        this.centerHandle = null;
        this.centerHandleSprite = null;

        this.limits = {
            min: 20,
            max: 500,
            distance: 300
        }

        this.model = model;
        this.useIcon = true;
        this.customIcon = false;

        // INIT
        this.camera !== null ? this._setInitialPosRot() : this.setPosition(x, y, z);

        this.add(this.mesh);

        this.mesh.groupDetails = { title: $trans('image-link'), icon: 'link', editTitle: $trans('edit-image-link'), dataLang: 'edit-image-link' }

        this.editControls();
        this.applyPerspectiveDragAndDrop();
        this.updateDragBehavior(true);

        //result
        return this;
    }

    setDefaultTexture = () => {
        if (this.dbConfig) {
            this.dbConfig.image = { url: '', aspectRatio: this.dbConfig.image?.aspectRatio || 1 };
        }
        this._loadedImageUrl = null;
        this._loadedAspectRatio = null;
        this.setUseIcon(true);
    }

    applyIndicatorHitGeometry = () => {
        this.updateMeshGeometry(INDICATOR_HIT_SIZE, INDICATOR_HIT_SIZE);
        const half = INDICATOR_HIT_SIZE / 2;
        this.perspectivePosition = {
            topLeft: new Vector3(-half, half, 0),
            topRight: new Vector3(half, half, 0),
            bottomRight: new Vector3(half, -half, 0),
            bottomLeft: new Vector3(-half, -half, 0),
        };
        this.applyPerspectiveDragAndDrop();
    }

    restorePerspectiveFromDbConfig = () => {
        ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'].forEach((name) => {
            const prop = this.dbConfig?.[name];
            if (prop) {
                this.perspectivePosition[name] = new Vector3(prop.x, prop.y, prop.z || 0);
            }
            const orig = this.dbConfig?.original?.[name];
            if (orig) {
                this.originalPerspectivePosition[name] = new Vector3(orig.x, orig.y, orig.z || 0);
            }
        });
    }

    isIndicatorMode = () => {
        return !!this.useIcon || !this.customIcon;
    }

    /**
     * Mesh-mode hotspot only. Icon mode is hinted via the 2D indicator.
     */
    isClickableInView() {
        if (this.model?.editMode) return false;
        if (this.isIndicatorMode()) return false;
        if (!this.mesh || this.mesh.visible === false) return false;
        if (this.userData?.hiddenBySceneAction) return false;
        if (this.dbConfig?.hideOnStart) return false;
        const mat = this.mesh.material;
        if (mat && !Array.isArray(mat) && typeof mat.opacity === 'number' && mat.opacity <= 0.01) {
            return false;
        }
        return true;
    }

    setUseIcon = (useIcon) => {
        const next = !!useIcon;
        if (this.useIcon === next) {
            this.customIcon = !next;
            return;
        }

        this.useIcon = next;
        this.customIcon = !next;
        if (this.dbConfig) this.dbConfig.useIcon = this.useIcon;

        if (this.useIcon) {
            this.updateDragBehavior();
            this.updateMeshTexture();
            return;
        }

        this.restorePerspectiveFromDbConfig();
        this.updateDragBehavior();
        this.updateMeshTexture();
    }

    updateDragBehavior = (isInitializing = false) => {
        if (this.useIcon) {
            this.objConfig.dragOnlyWhenSelected = false;
            if (this.model?.editMode) {
                if (!isInitializing) {
                    this.applyIndicatorHitGeometry();
                    this.removeDragPoints();
                }
                if (this.isSelected) {
                    this.hideDragHandles();
                }
            }
            return;
        }

        this.objConfig.dragOnlyWhenSelected = true;
        if (this.model?.editMode) {
            if (!isInitializing) {
                this.addDragPoints();
            }
            if (this.isSelected) {
                this.showDragHandles();
            }
        }
    }

    getCustomImageUrl = () => {
        const url = this.dbConfig?.image?.url;
        return url && !String(url).includes('webLinkHotSpot.png') ? url : null;
    }

    updateMeshTexture = (imageLoadCallback = null, resetPerspective = false) => {
        if (this.useIcon) {
            if (this.model?.editMode) {
                this.mesh.material.map = emptyTexture;
                this.applyIndicatorHitGeometry();
            } else {
                this.mesh.material.map = linkTexture;
                this.mesh.material.needsUpdate = true;
                this.applyPerspectiveDragAndDrop();
            }
            this.mesh.material.needsUpdate = true;
            imageLoadCallback && imageLoadCallback();
            return;
        }

        const url = this.getCustomImageUrl();
        if (url) {
            if (this._loadedImageUrl === url && this.mesh.material.map && this.mesh.material.map !== emptyTexture) {
                this.applyPerspectiveDragAndDrop();
                if (this.model?.editMode) {
                    this.addDragPoints();
                    if (this.isSelected) this.showDragHandles();
                }
                imageLoadCallback && imageLoadCallback();
                return;
            }
            this.loadCustomImage(url, imageLoadCallback, resetPerspective);
            return;
        }

        if (this.model?.editMode) {
            this.mesh.material.map = emptyTexture;
            this.mesh.material.needsUpdate = true;
        } else {
            this.mesh.material.map = linkTexture;
            this.mesh.material.needsUpdate = true;
        }
        this.applyPerspectiveDragAndDrop();
        imageLoadCallback && imageLoadCallback();
    }

    loadCustomImage = (url, callback = null, resetPerspective = false) => {
        const apply = (texture) => {
            const imageAspectRatio = this.applyTextureAndGeometry(texture, url, this.dbConfig?.image?.aspectRatio || 1, resetPerspective);
            this._loadedImageUrl = url;
            this._loadedAspectRatio = imageAspectRatio;
            this.removeDragPoints();
            this.applyPerspectiveDragAndDrop();
            if (this.model?.editMode) {
                this.addDragPoints();
                if (this.isSelected) this.showDragHandles();
            }
            callback && callback();
        };

        if (meshTextures[url]) {
            apply(meshTextures[url]);
            return;
        }

        textureLoader.load(url, (texture) => {
            meshTextures[url] = texture;
            apply(texture);
        });
    }

    updateMeshGeometry = (width, height) => {
        if (this.mesh.geometry) {
            this.mesh.geometry.dispose();
        }
        this.mesh.geometry = new THREE.PlaneGeometry(width, height);
        this.mesh.geometry.computeBoundingBox();
        this.mesh.geometry.computeBoundingSphere();
        this.mesh.updateMatrixWorld(true);
    }

    resetPerspectivePositions = (width, height) => {
        const halfW = width / 2;
        const halfH = height / 2;
        const corners = {
            topLeft: new Vector3(-halfW, halfH, 0),
            topRight: new Vector3(halfW, halfH, 0),
            bottomRight: new Vector3(halfW, -halfH, 0),
            bottomLeft: new Vector3(-halfW, -halfH, 0),
        };

        if (!this.dbConfig.original) this.dbConfig.original = {};
        Object.entries(corners).forEach(([name, vector]) => {
            this.perspectivePosition[name] = vector.clone();
            this.dbConfig[name] = vector.clone();
            this.dbConfig.original[name] = vector.clone();
            this.originalPerspectivePosition[name] = vector.clone();
        });
    }

    resetPerspective = () => {
        this.removeDragPoints();

        if (this.useIcon) {
            this.applyIndicatorHitGeometry();
            return;
        }

        const texture = this.mesh?.material?.map;
        const imageWidth = texture?.image?.width;
        const imageHeight = texture?.image?.height;

        if (imageWidth && imageHeight) {
            this.updateMeshGeometry(imageWidth, imageHeight);
            this.resetPerspectivePositions(imageWidth, imageHeight);
        } else {
            this.updateMeshGeometry(64, 64);
            this.resetPerspectivePositions(64, 64);
        }

        this.applyPerspectiveDragAndDrop();
        if (this.model?.editMode) {
            this.addDragPoints();
            if (this.isSelected && this.dbConfig?.showDragCorners !== false) {
                this.showDragHandles();
            }
        }
    }

    hasSavedPerspectivePositions = () => {
        const corners = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
        return corners.every((name) => {
            const p = this.dbConfig?.[name];
            return p && Number.isFinite(p.x) && Number.isFinite(p.y);
        });
    }

    setGeometryFromTexture = (texture, src, fallbackAspectRatio = 1, resetPerspective = true) => {
        const imageWidth = texture?.image?.width;
        const imageHeight = texture?.image?.height;

        if (imageWidth && imageHeight) {
            const imageAspectRatio = imageWidth / imageHeight;
            this.updateMeshGeometry(imageWidth, imageHeight);
            if (resetPerspective) {
                this.resetPerspectivePositions(imageWidth, imageHeight);
            }
            return imageAspectRatio;
        }

        const imageAspectRatio = fallbackAspectRatio > 0 ? fallbackAspectRatio : 1;
        const fallbackHeight = 1000;
        const fallbackWidth = Math.max(1, fallbackHeight * imageAspectRatio);
        this.updateMeshGeometry(fallbackWidth, fallbackHeight);
        if (resetPerspective) {
            this.resetPerspectivePositions(fallbackWidth, fallbackHeight);
        }
        return imageAspectRatio;
    }

    applyTextureAndGeometry = (texture, src, fallbackAspectRatio = 1, resetPerspective = true) => {
        this.mesh.material.map = texture;
        this.mesh.material.needsUpdate = true;
        const imageAspectRatio = this.setGeometryFromTexture(texture, src, fallbackAspectRatio, resetPerspective);
        return imageAspectRatio;
    }

    setCustomTexture = (link) => {
        if (meshTextures[link]) {
            this.applyTextureAndGeometry(meshTextures[link], link);
            return;
        }

        textureLoader.load(link, (texture) => {
            meshTextures[link] = texture;
            this.applyTextureAndGeometry(texture, link);
        });
    }

    setLink = (link) => {
        this.dbConfig.url = link;
        this.link = link;
    }

    getLink = () => {
        return this.link;
    }

    setDbConfig(config) {
        super.setDbConfig(config);
        ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'].forEach((name) => {
            const prop = this.dbConfig[name];
            if (prop && (prop.x !== undefined && prop.y !== undefined)) {
                this.perspectivePosition[name] = new Vector3(prop.x, prop.y, prop.z || 0);
            }
        });

        if (config.useIcon !== undefined) {
            this.useIcon = config.useIcon === true;
        } else if (config.image !== undefined) {
            this.useIcon = !config.image?.url;
        }
        this.customIcon = this.useIcon === false;
        this.dbConfig.useIcon = this.useIcon;

        if (config.openNewWindow !== undefined) {
            this.dbConfig.openNewWindow = config.openNewWindow;
        }

        this.updateDragBehavior();
        this.updateMeshTexture(null, !this.hasSavedPerspectivePositions());
    }

    // ===== Drag handles and perspective deformation (matching VRImageGallery) =====
    onSelect() {
        if (this.useIcon || !this.customIcon) {
            super.onSelect();
            return;
        }
        if (!this.dragCubeCorners["topLeft"]) {
            this.addDragPoints();
        }
        this.showDragHandles();
        super.onSelect();
    }

    onDeselect() {
        this.hideDragHandles();
        super.onDeselect();
    }

    showDragHandles() {
        if (this.useIcon) return;
        Object.values(this.dragSprites).forEach(sprite => {
            if (sprite) sprite.visible = true;
        });
        Object.values(this.dragCubeCorners).forEach(cube => {
            if (cube) cube.visible = true;
        });
        if (this.centerHandle) this.centerHandle.visible = true;
        if (this.centerHandleSprite) this.centerHandleSprite.visible = true;
    }

    hideDragHandles() {
        Object.values(this.dragSprites).forEach(sprite => {
            if (sprite) sprite.visible = false;
        });
        Object.values(this.dragCubeCorners).forEach(cube => {
            if (cube) cube.visible = false;
        });
        if (this.centerHandle) this.centerHandle.visible = false;
        if (this.centerHandleSprite) this.centerHandleSprite.visible = false;
    }

    syncHandleVisuals = () => {
        const sx = this.mesh?.scale?.x || 1;
        const sy = this.mesh?.scale?.y || 1;
        const sz = this.mesh?.scale?.z || 1;
        const invScale = new Vector3(
            sx !== 0 ? 1 / sx : 1,
            sy !== 0 ? 1 / sy : 1,
            sz !== 0 ? 1 / sz : 1
        );

        Object.values(this.dragCubeCorners).forEach((cube) => {
            if (!cube) return;
            cube.scale.copy(invScale);
            cube.renderOrder = 10000;
        });

        if (this.centerHandle) {
            this.centerHandle.scale.copy(invScale);
            this.centerHandle.renderOrder = 10000;
        }
    }

    addDragPoints() {
        if (this.useIcon) return;
        if (this.dragCubeCorners["topLeft"]) return;

        const sphereRadius = this.objConfig.cubeSize / 2;
        const hitRadius = sphereRadius * (this.objConfig.cornerHitAreaScale || 1);
        const cubeGeometry = new SphereGeometry(hitRadius, 8, 8);
        const cubeMaterial = new MeshBasicMaterial({
            color: 0x6B9D11,
            opacity: 0,
            transparent: true,
            depthWrite: false,
            depthTest: false,
            renderOrder: 999,
        });

        const positionAttribute = this.mesh.geometry.attributes.position;
        if (!positionAttribute || positionAttribute.count < 4) return;

        const cornerNames = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
        const cornerIndices = [0, 1, 3, 2];

        this.dragCubeCorners = {};

        cornerIndices.forEach((index, i) => {
            const offset = this.objConfig.offset[cornerNames[i]];
            const x = positionAttribute.getX(index) + (offset?.x || 0);
            const y = positionAttribute.getY(index) + (offset?.y || 0);
            const z = positionAttribute.getZ(index);

            const cube = new Mesh(cubeGeometry, cubeMaterial);
            cube.name = cornerNames[i];
            cube.position.set(x, y + this.objConfig.cubeSize / 2, z + 1);
            cube.renderOrder = 10000;

            const spriteRadius = this.objConfig.cubeSize / 2 * 1.1;
            const spriteGeometry = new SphereGeometry(spriteRadius, 16, 16);
            const spriteMaterial = new MeshBasicMaterial({
                color: 0x6B9D11,
                transparent: true,
                opacity: 0.5,
                depthTest: false,
                depthWrite: false
            });
            const sprite = new Mesh(spriteGeometry, spriteMaterial);
            sprite.name = `sprite_${cornerNames[i]}`;
            sprite.position.set(0, -this.objConfig.cubeSize / 2, 0);
            sprite.raycast = () => { };
            sprite.renderOrder = 10001;

            const spriteIconMaterial = new SpriteMaterial({
                map: myLocationTexture,
                depthTest: false,
                transparent: true
            });
            const spriteIcon = new Sprite(spriteIconMaterial);
            spriteIcon.name = `spriteIcon_${cornerNames[i]}`;
            spriteIcon.position.set(0, -this.objConfig.cubeSize / 2, 0.1);
            spriteIcon.scale.set(this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2);
            spriteIcon.raycast = () => { };
            spriteIcon.renderOrder = 10002;

            cube.add(sprite);
            cube.add(spriteIcon);

            this.dragSprites[sprite.name] = sprite;
            this.mesh.add(cube);
            this.dragCubeCorners[cube.name] = cube;
        });

        // Center handle
        const centerGeometry = new SphereGeometry(hitRadius, 8, 8);
        const centerMaterial = new MeshBasicMaterial({
            color: 0x6B9D11,
            opacity: 0,
            transparent: true
        });

        this.centerHandle = new Mesh(centerGeometry, centerMaterial);
        this.centerHandle.name = 'centerHandle';
        this.centerHandle.position.set(0, 0, 1);
        this.centerHandle.raycast = () => { };
        this.centerHandle.renderOrder = 10000;

        const centerSpriteRadius = sphereRadius * 1.1;
        const centerSpriteGeometry = new SphereGeometry(centerSpriteRadius, 16, 16);
        const centerSpriteMaterial = new MeshBasicMaterial({
            color: 0x6B9D11,
            transparent: true,
            opacity: 0.5,
            depthTest: false,
            depthWrite: false
        });
        this.centerHandleSprite = new Mesh(centerSpriteGeometry, centerSpriteMaterial);
        this.centerHandleSprite.name = 'sprite_centerHandle';
        this.centerHandleSprite.position.set(0, 0, 0);
        this.centerHandleSprite.raycast = () => { };
        this.centerHandleSprite.renderOrder = 10001;

        const centerSpriteIconMaterial = new SpriteMaterial({
            map: dragTexture,
            depthTest: false,
            transparent: true
        });
        this.centerHandleSpriteIcon = new Sprite(centerSpriteIconMaterial);
        this.centerHandleSpriteIcon.name = 'spriteIcon_centerHandle';
        this.centerHandleSpriteIcon.position.set(0, 0, 0.1);
        this.centerHandleSpriteIcon.scale.set(sphereRadius, sphereRadius, sphereRadius);
        this.centerHandleSpriteIcon.raycast = () => { };
        this.centerHandleSpriteIcon.renderOrder = 10002;

        this.centerHandle.add(this.centerHandleSprite);
        this.centerHandle.add(this.centerHandleSpriteIcon);
        this.mesh.add(this.centerHandle);
        this.syncHandleVisuals();

        this.hideDragHandles();
    }

    removeDragPoints() {
        Object.entries(this.dragSprites).forEach(([name]) => {
            if (this.dragSprites[name]) {
                this.dragSprites[name].parent?.remove(this.dragSprites[name]);
                this.dragSprites[name] = null;
            }
        });
        Object.entries(this.dragCubeCorners).forEach(([name]) => {
            this.mesh.remove(this.dragCubeCorners[name]);
            this.dragCubeCorners[name] = null;
        });
        if (this.centerHandle) {
            this.mesh.remove(this.centerHandle);
            this.centerHandle = null;
        }
        if (this.centerHandleSprite) this.centerHandleSprite = null;
        if (this.centerHandleSpriteIcon) this.centerHandleSpriteIcon = null;
    }

    updateLivePosition(name, position) {
        this.dbConfig[name] = this.perspectivePosition[name].clone();
        this.perspectivePosition[name].copy(position);
        this.applyPerspectiveDragAndDrop();
    }

    updateMeshCorner(cornerName, newPosition) {
        const positionAttribute = this.mesh.geometry.attributes.position;
        const cornerMappings = {
            topLeft: 0,
            topRight: 1,
            bottomRight: 3,
            bottomLeft: 2
        };

        const vertex = vertexLocalFromCornerHandle(newPosition, this.objConfig, cornerName);
        const vertexPosition = new Vector3(vertex.x, vertex.y, vertex.z);

        this.perspectivePosition[cornerName] = vertexPosition;
        this.dbConfig[cornerName] = vertexPosition.clone();

        const vertexIndex = cornerMappings[cornerName];
        if (vertexIndex !== undefined) {
            positionAttribute.setXYZ(
                vertexIndex,
                vertexPosition.x,
                vertexPosition.y,
                vertexPosition.z
            );
            positionAttribute.needsUpdate = true;
            this.mesh.geometry.computeBoundingBox();
            this.mesh.geometry.computeBoundingSphere();
        }
    }

    applyPerspectiveDragAndDrop() {
        const points = this.perspectivePosition;
        const positionAttribute = this.mesh.geometry.attributes.position;
        const uvAttribute = this.mesh.geometry.attributes.uv;

        const cornerMappings = {
            topLeft: 0,
            topRight: 1,
            bottomRight: 3,
            bottomLeft: 2
        };

        Object.entries(cornerMappings).forEach(([corner, index]) => {
            if (points[corner]) {
                positionAttribute.setXY(index, points[corner].x, points[corner].y);
                if (this.dragCubeCorners && this.dragCubeCorners[corner]) {
                    const handle = cornerHandleLocalFromVertex(points[corner], this.objConfig, corner);
                    this.dragCubeCorners[corner].position.set(handle.x, handle.y, handle.z);
                }
            }
        });

        uvAttribute.setXY(cornerMappings['topLeft'], 0, 1);
        uvAttribute.setXY(cornerMappings['topRight'], 1, 1);
        uvAttribute.setXY(cornerMappings['bottomRight'], 1, 0);
        uvAttribute.setXY(cornerMappings['bottomLeft'], 0, 0);

        positionAttribute.needsUpdate = true;
        uvAttribute.needsUpdate = true;
        this.syncHandleVisuals();

        this.mesh.geometry.computeBoundingBox();
        this.mesh.geometry.computeBoundingSphere();
    }

}

ComponentRegistry.register({
    type: 'webLink',
    displayNameKey: 'image-link',
    getDisplayName: (obj) => {
        const n = obj?.name != null ? String(obj.name).trim() : '';
        return n || $trans('image-link') || 'Link';
    },
    events: [
        { value: 'click', labelKey: 'event-click' },
        { value: 'raycastEnter', labelKey: 'event-raycast-enter' },
        { value: 'raycastLeave', labelKey: 'event-raycast-leave' },
    ],
    actions: [{ value: 'openLink', labelKey: 'action-open-link' }],
});

if (typeof window !== 'undefined') {
    window.VRLinkObject_transformImageFileForUpload = VRLinkObject.transformImageFileForUpload;
}

export { VRLinkObject };